// Injuries & Suspension — availability list.
//
// The first widget cut over to app-api (M2 slice 4): it fetches its dataset via
// `useWidgetData("injuries-suspension", useFixtureKey(fixture))` instead of reading a legacy
// dummy-data getter. The pure shaper (`sortInjuryPlayers`) and
// the loaded markup are unchanged — it still emits the SAME DOM + class names
// as the legacy renderer, so shared CSS renders it identically (the visual A/B
// baseline is unchanged). What's new is the async lifecycle the synchronous
// getter never had: a skeleton while pending and a retry on error.
//
// The endpoint is fixture-scoped; injuries aren't fixture-specific, so the
// seeder materializes the same payload under every fixture (m2-plan §3). We
// pass the current fixture's slug so the read is a single GetItem. The team
// control (a legacy shell select) still arrives via the bridge context.

import { EMPTY_INJURY, sortInjuryPlayers } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface InjurySuspensionProps {
  /** Active fixture label (e.g. "Arsenal v Everton"); slugified for the endpoint. */
  fixture: string;
  /** Selected team (full name), from the team-toggle; falls back to Arsenal. */
  team?: string | null;
}

export default function InjurySuspension({ fixture, team }: InjurySuspensionProps) {
  const selected = team || "Arsenal";
  const {
    data: injuries,
    isPending,
    isError,
    refetch,
  } = useWidgetData("injuries-suspension", useFixtureKey(fixture));

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const data = injuries[selected] ?? EMPTY_INJURY;
  const players = sortInjuryPlayers(data.players);

  return (
    <div className="data-wrapper top-align">
      <div className="bm-updated">Updated: {data.updated}</div>
      {players.length ? (
        players.map((p, i) => (
          <div className="data-row" key={`${p.name}-${i}`}>
            <div className="col-team">{p.name}</div>
            <span
              className="inj-badge"
              style={{
                background: `${p.colour}22`,
                color: p.colour,
                border: `1px solid ${p.colour}44`,
              }}
            >
              {p.status}
            </span>
          </div>
        ))
      ) : (
        <div className="bm-empty">No injuries or suspensions reported.</div>
      )}
    </div>
  );
}
